/* Gráficos personalizados no modo demonstração: mesma agregação de app/whitelabel/graficos.py. */

import { MATERIAIS } from './materiais';
import type { LinhaDemo } from './dados';
import type { Agregado, ConfigGrafico, GraficoSalvo, OpcoesGraficos, TipoGrafico } from '../tipos/api';

const MESES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const METRICAS: Record<string, [string, string, (l: LinhaDemo) => number]> = {
  total: ['Emissões totais', 'tCO₂e', (l) => l.total],
  co2: ['CO₂', 't', (l) => l.co2],
  ch4: ['CH₄', 't', (l) => l.ch4],
  n2o: ['N₂O', 't', (l) => l.n2o],
  biogenico: ['CO₂ biogênico', 't', (l) => l.biogenica],
  lancamentos: ['Número de lançamentos', 'lançamentos', () => 1],
};

const nomeMaterial = (l: LinhaDemo) => MATERIAIS.find((m) => m.id === l.id_material)?.nome || 'Sem material';

const DIMENSOES: Record<string, [string, (l: LinhaDemo) => string]> = {
  mes: ['Mês', (l) => MESES[Number(l.data.slice(5, 7)) - 1]],
  obra: ['Obra', (l) => l.obra || 'Sem obra informada'],
  escopo: ['Escopo', (l) => (l.escopo ? `Escopo ${l.escopo}` : 'Sem escopo')],
  categoria: ['Categoria', (l) => l.categoria],
  categoria_geral: ['Categoria geral', (l) => l.categoria_geral],
  fornecedor: ['Fornecedor (CNPJ)', (l) => l.cnpj_fornecedor || 'Sem fornecedor'],
  descricao: ['Descrição do lançamento', (l) => l.descricao || 'Sem descrição'],
  material: ['Material comprado', nomeMaterial],
};

const TIPOS: Record<TipoGrafico, string> = {
  barras: 'Barras', barras_horizontais: 'Barras horizontais', linhas: 'Linhas', pizza: 'Pizza', tabela: 'Tabela',
};

export const OPCOES_GRAFICOS: OpcoesGraficos = {
  metricas: Object.entries(METRICAS).map(([id, [nome, unidade]]) => ({ id, nome, unidade })),
  dimensoes: Object.entries(DIMENSOES).map(([id, [nome]]) => ({ id, nome })),
  tipos: (Object.entries(TIPOS) as [TipoGrafico, string][]).map(([id, nome]) => ({ id, nome })),
};

export class ErroGrafico extends Error {}

export function validarConfig(c: Partial<ConfigGrafico> & Record<string, unknown>): ConfigGrafico {
  const metrica = String(c.metrica || 'total');
  const dimensao = String(c.dimensao || '');
  const dimensao2 = String(c.dimensao2 || '') || null;
  const tipo = String(c.tipo || 'barras') as TipoGrafico;
  if (!METRICAS[metrica]) throw new ErroGrafico(`Métrica inválida: ${metrica}.`);
  if (!DIMENSOES[dimensao]) throw new ErroGrafico(`Agrupamento inválido: ${dimensao || '(vazio)'}.`);
  if (dimensao2 && !DIMENSOES[dimensao2]) throw new ErroGrafico(`Segunda divisão inválida: ${dimensao2}.`);
  if (dimensao2 === dimensao) throw new ErroGrafico('A segunda divisão precisa ser diferente do agrupamento.');
  if (!TIPOS[tipo]) throw new ErroGrafico(`Tipo de gráfico inválido: ${tipo}.`);
  if (tipo === 'pizza' && dimensao2) throw new ErroGrafico('Gráfico de pizza não aceita segunda divisão.');
  const filtros = (c.filtros || {}) as ConfigGrafico['filtros'];
  return {
    metrica, dimensao, dimensao2, tipo,
    limite: Math.max(1, Math.min(Number(c.limite) || 12, 50)),
    filtros: { ...(filtros.escopo ? { escopo: String(filtros.escopo) } : {}), ...(filtros.categoria ? { categoria: String(filtros.categoria) } : {}) },
  };
}

export function agregar(linhas: LinhaDemo[], config: Partial<ConfigGrafico> & Record<string, unknown>): Agregado {
  const cfg = validarConfig(config);
  let ls = linhas;
  if (cfg.filtros.escopo) ls = ls.filter((l) => String(l.escopo) === cfg.filtros.escopo);
  if (cfg.filtros.categoria) ls = ls.filter((l) => l.categoria === cfg.filtros.categoria);
  if ([cfg.dimensao, cfg.dimensao2].includes('material')) ls = ls.filter((l) => l.id_categoria === 38);

  const valor = METRICAS[cfg.metrica][2];
  const grupoDe = DIMENSOES[cfg.dimensao][1];
  const serieDe = cfg.dimensao2 ? DIMENSOES[cfg.dimensao2][1] : () => null;
  const somas = new Map<string, Map<string | null, number>>();
  ls.forEach((l) => {
    const g = grupoDe(l), s = serieDe(l);
    const m = somas.get(g) || new Map<string | null, number>();
    m.set(s, (m.get(s) || 0) + valor(l));
    somas.set(g, m);
  });
  const totalGrupo = (g: string) => [...(somas.get(g)?.values() || [])].reduce((a, v) => a + v, 0);

  let grupos: string[];
  if (cfg.dimensao === 'mes') grupos = [...MESES];
  else {
    grupos = [...somas.keys()].sort((a, b) => (cfg.dimensao === 'escopo' ? a.localeCompare(b) : totalGrupo(b) - totalGrupo(a)));
    if (grupos.length > cfg.limite) {
      const resto = grupos.slice(cfg.limite - 1);
      const outros = new Map<string | null, number>();
      resto.forEach((g) => somas.get(g)?.forEach((v, s) => outros.set(s, (outros.get(s) || 0) + v)));
      resto.forEach((g) => somas.delete(g));
      somas.set('Outros', outros);
      grupos = [...grupos.slice(0, cfg.limite - 1), 'Outros'];
    }
  }
  const totalSerie = new Map<string | null, number>();
  grupos.forEach((g) => somas.get(g)?.forEach((v, s) => totalSerie.set(s, (totalSerie.get(s) || 0) + v)));
  let series: (string | null)[] = cfg.dimensao2 ? [...totalSerie.keys()].sort((a, b) => (totalSerie.get(b) || 0) - (totalSerie.get(a) || 0)) : [null];
  if (cfg.dimensao2 === 'mes') series = MESES.filter((m) => totalSerie.has(m));
  if (cfg.dimensao2 === 'escopo') series = [...series].sort((a, b) => String(a).localeCompare(String(b)));

  const itens = grupos.flatMap((g) => series.map((s) => ({ grupo: g, serie: s, valor: somas.get(g)?.get(s) || 0 })));
  const [metrica_nome, unidade] = METRICAS[cfg.metrica];
  return {
    ...cfg, metrica_nome, unidade, dimensao_nome: DIMENSOES[cfg.dimensao][0], dimensao2_nome: cfg.dimensao2 ? DIMENSOES[cfg.dimensao2][0] : null,
    grupos, series, itens, total: itens.reduce((a, i) => a + i.valor, 0), lancamentos: ls.length,
  };
}

const exemplo = (id: string, ordem: number, titulo: string, config: Partial<ConfigGrafico>): GraficoSalvo => ({
  id, ordem, titulo, created_at: '2026-03-01T10:00:00', config: validarConfig(config),
});

/** Gráficos já salvos no modo demonstração (exemplos do que o usuário pode montar). */
export const GRAFICOS_SALVOS: GraficoSalvo[] = [
  exemplo('graf-1', 1, 'Emissões por obra e escopo', { metrica: 'total', dimensao: 'obra', dimensao2: 'escopo', tipo: 'barras_horizontais' }),
  exemplo('graf-2', 2, 'Evolução mensal por escopo', { metrica: 'total', dimensao: 'mes', dimensao2: 'escopo', tipo: 'linhas' }),
  exemplo('graf-3', 3, 'Materiais que mais emitem', { metrica: 'total', dimensao: 'material', tipo: 'pizza' }),
  exemplo('graf-4', 4, 'Categorias de emissão por mês', { metrica: 'total', dimensao: 'mes', dimensao2: 'categoria_geral', tipo: 'barras' }),
];

let seq = 100;

export function salvarGrafico(titulo: unknown, config: unknown): GraficoSalvo {
  const t = String(titulo || '').trim();
  if (!t) throw new ErroGrafico('Dê um título ao gráfico.');
  if (t.length > 120) throw new ErroGrafico('O título passa de 120 caracteres.');
  const g: GraficoSalvo = {
    id: `graf-${++seq}`, titulo: t, created_at: new Date().toISOString(),
    config: validarConfig((config || {}) as Record<string, unknown>),
    ordem: Math.max(0, ...GRAFICOS_SALVOS.map((x) => x.ordem)) + 1,
  };
  GRAFICOS_SALVOS.push(g);
  return g;
}

export function apagarGrafico(id: string): boolean {
  const i = GRAFICOS_SALVOS.findIndex((g) => g.id === id);
  if (i < 0) return false;
  GRAFICOS_SALVOS.splice(i, 1);
  return true;
}
